import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { StorageService } from '../../services/storage/storage.service';
import { environment}  from '../../../environments/environment';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { Platform } from '@ionic/angular';
import OneSignal from 'onesignal-cordova-plugin';

export class Credentials {
  Email: string = "";
  Password: string = "";
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null); // For HT-Auth Pages
  session_token: BehaviorSubject<string> = new BehaviorSubject<string>(null); // For PressRoom Pages

  private auth_portal: HttpClient;  

  showPriorityPlayers: BehaviorSubject<boolean>;

  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private storage: StorageService,
    private device: Device,
    private platform: Platform
  ) {
    this.storage.get("Auth_Token").then(token => {
      if(token) {
        this.isAuthenticated.next(true);
      }
    });

    this.storage.get("Session_Token").then(token => {
      if(token) {
        this.session_token.next(token);
      }
    });

    this.showPriorityPlayers = new BehaviorSubject<boolean>(false);

    this.auth_portal = new HttpClient(this.handler); // Requests to auth portal will avoid the jwt-interceptor by passing in this handler
  }

  /**
   * Gets the auth token from HT-Auth
   */
  login(credentials: Credentials, deviceId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}login`, { email: credentials.Email, password: credentials.Password, device_id: deviceId }, { headers: headers }).pipe(
        map((data: any) => data),
        switchMap (data => {
          if (data.token != "") {
            this.storage.set('Auth_Token', data.token); // Store session token 
            this.storage.set('loggedInAs', credentials.Email); // Store email
            this.isAuthenticated.next(true);        
          }
          return of(data);
        }), catchError(err => {
          return of(err.error.message);
        })
    );
  }

    /**
   * Validates the auth code from HT-Auth
   */
  validateAuthCode(email: string, deviceId: string, authCode: string, trustDevice: boolean): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}validateAuthCode`, { email: email, device_id: deviceId, auth_code: authCode, trust_device: trustDevice }, { headers: headers }).pipe(
      map((data: any) => {
          if (data.token != "") {
            this.storage.set('Auth_Token', data.token); // Store session token 
            this.storage.set('loggedInAs', email); // Store email
            this.isAuthenticated.next(true);           
          }
          return data;
        }), catchError(err => {
          return err.error;
        })
    );
  }

  /**
 * Validates the verification code from HT-Auth
 */
  validateCode(email: string, deviceId: string, code: string, auth_token: string): Observable<any> {
    const headers = { 'Authorization': auth_token };

    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}validateCode`, { email: email, device_id: deviceId, code: code }, { headers }).pipe(
      map((data: any) => {
        return data; 
      }),
      catchError(err => {
        return err.error;
      })
    );
  }

  async logout() {
    this.storage.clear().then(() => { // Clear all stored data
      this.isAuthenticated.next(false); // Update ht auth guard 
    });

    if (this.platform.is("cordova")) {
      OneSignal.User.pushSubscription.optOut();
      OneSignal.logout();
    }
  }

  resetPassword(email: string) {
    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}requestPasswordReset`, { email: email }).pipe(
      map(data => {
        return data;
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  /**
   * Get all of the user's services (this will only get press room services)
   */
  getUserServices(auth_token: string) {
    const headers = new HttpHeaders({
      'Authorization': auth_token
    });

    return this.auth_portal.get<any>(`${environment.AUTHPORTAL_API_URL}services`, { headers });
  }

  /**
   * Get token used to login from the auth portal (user_services_token) (requires Auth_Token)
  */
  getToken(user_service_id: number, auth_token: string) {
    const headers = { 'Authorization': auth_token };

    return this.auth_portal.get(`${environment.AUTHPORTAL_API_URL}services/token?userServiceId=${user_service_id.toString()}`, { headers });
  }

  requestNewEmailVerification(deviceId: string, email: string, newEmail: string, auth_token: string): Observable<any> {
    const headers = { 'Authorization': auth_token };
    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}account/requestNewEmailVerification`, { device_id: deviceId, email: email, new_email: newEmail }, { headers }).pipe(
      map((data: any) => {
        return data; 
      }),
      catchError(err => {
        return err.error;
      })
    );
  }

  requestNewCellVerification(deviceId: string, email: string, cell: string, auth_token: string): Observable<any> {
    const headers = { 'Authorization': auth_token };
    return this.auth_portal.post(`${environment.AUTHPORTAL_API_URL}account/requestNewCellVerification`, { device_id: deviceId, email: email, cell: cell }, { headers }).pipe(
      map((data: any) => {
        return data; 
      }),
      catchError(err => {
        return err.error;
      })
    );
  }

  /**
   * Update User Info (requires Auth_Token)
  */
   updateUser(userServiceId: number, userInfo: any, auth_token: string): Observable<any> {
    const headers = { 'Authorization': auth_token };
    var body = userInfo;
    body.apply_all = true;

    const url = environment.AUTHPORTAL_API_URL + `userInfo/${userServiceId.toString()}`;
    return this.auth_portal.put(url, body, { headers }).pipe(
      map((data: any) => {
        return data; 
      }),
      catchError(err => {
        return err.error;
      })
    );
  } 

  /**
   * Login from the auth portal. Requires token retrieved from getToken(user_service_id, auth_token)
   * Returns the Session_Token
   */
  signIntoPressRoom(database, username, token) {
    return this.http.post(`${environment.PRESSROOM_API_URL}users/loginFromAuthPortal`, { database: database, token: token, username: username }).pipe( // Use pipe (rxjs) to edit data before subscription
      map((data: any) => data.token), // Extract token from JSON data
      switchMap (sessionToken => { // Intercept token and store relevant data in local storage before returning an observable of the token
        return this.storage.removeMultipleValues(
          ["DATABASE",
          "USERNAME",
          "token",
          "Session_Token",
          "dark_mode"]
        ).then(() => {
          this.storage.set("DATABASE", database);
          this.storage.set("USERNAME", username);
          this.storage.set("token", token);
          this.storage.set('Session_Token', sessionToken); // Store session token
          this.session_token.next(sessionToken);
          return of(sessionToken);
        });
      })
    );
  }

  /**
   * Check AppUsers table for user
   */
  checkForUser() {

    return this.http.get(`${environment.PRESSROOM_API_URL}appUser`).pipe(
      map((data: any) => {
        if(data) {
          return data;
        } else {
          return false;
        }
      })
    );
  }

  checkForDevice(deviceId: string, appUserId: string) {
    const params = {
      'device_id': deviceId.toString(),
      'app_user_id': appUserId.toString()
    }

    return this.http.get(`${environment.PRESSROOM_API_URL}appDevice?parameters=` + JSON.stringify(params)).pipe(
      map((data: any) => data),
      switchMap((deviceTypeId) => {
        return of(deviceTypeId);
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  getDevice(deviceId: string, appUserId: string)  {
    return this.storage.get("device").then(device => {
      if(device) {
        if (device.priority_players) {
          this.showPriorityPlayers.next(device.priority_players);
        }
        return of(device);
      } else {
        const params = {
          'device_id': deviceId.toString(),
          'app_user_id': appUserId.toString()
        }

        return this.http.get(`${environment.PRESSROOM_API_URL}appDevice?parameters=` + JSON.stringify(params)).pipe(
          map((data: any) => {
            this.storage.set("device", data);
            if (device && device.priority_players) {
              this.showPriorityPlayers.next(device.priority_players);
            }
            return data;
          }),
          catchError(err => {
            return of(err.error);
          })
        );
      }
    });

  }

  getAppDevice(deviceId: string, appUserId: string) {
    const params = {
      'device_id': deviceId.toString(),
      'app_user_id': appUserId.toString()
    }

    return this.http.get(`${environment.PRESSROOM_API_URL}appDevice?parameters=` + JSON.stringify(params)).pipe(
      map((data: any) => {
        this.storage.set("device", data);
        return data;
      }),
      catchError(err => {
        return err.error;
      })
    );
  }

  updateDevice(device: any) {
    const body = {
      'device': device
    }

    this.storage.set("data_save", device.data_save_mode);

    this.storage.set("device", device);

    this.showPriorityPlayers.next(device.priority_players);

    return this.http.put(`${environment.PRESSROOM_API_URL}appDevice/${device.id}`, body).pipe(
      map((data: any) => data[0]),
      switchMap((deviceTypeId) => {
        return of(deviceTypeId);
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  /**
   * Add user to AppUsers table
   */
  addUser(databaseName: string) {
    return this.http.post(`${environment.PRESSROOM_API_URL}appUser`, { database: databaseName });
  }

  /**
   * Generates a unique ("cross-install, app-specific") ID for the device
   */
  generateDeviceId(platform: any, uid: string) {
    if(platform == "web") {
      return Promise.resolve(uid + 'web');
    } else {
      return Promise.resolve(this.device.uuid);
    }
  }

  /**
   * Get the id of the device type
   */
  getDeviceType(platform: string) {
    const params = {
      'device_name': platform,
    }

    return this.http.get(`${environment.PRESSROOM_API_URL}deviceTypes?parameters=` + JSON.stringify(params)).pipe(
      map((data: any) => data[0].id),
      switchMap((deviceTypeId) => {
        return of(deviceTypeId);
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  /**
   * Add device to AppDevices
   */
  addDevice(appUserId: string, id: string, deviceTypeId: string) {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
    return this.http.post(`${environment.PRESSROOM_API_URL}appDevice`, { 'app_user_id': appUserId.toString(), 'app_device_id': id.toString(), 'device_type_id': deviceTypeId.toString(), 'system_theme_pref': prefersDark.matches }).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  getSettingsRules() : Observable<any> {
    const url = environment.PRESSROOM_API_URL + `screenrules/settings`;

    return this.http.get(url).pipe(
      map((data: any) => data.settings_rules),
      switchMap((rules: any) => {
        return of(rules);
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  rememberMe(rememberMe: boolean, email: string = null) {
    if (rememberMe) {
      localStorage.setItem('rememberMe', email);
    } else {
      localStorage.removeItem('rememberMe');
    }
  }
}

